import React, { useRef, ReactNode } from 'react';
import TextStyle from '@able/react/dist/TextStyle';
import './style.scss';
import Spacing from '@able/react/dist/Spacing';
import Divider from '@able/react/dist/Divider';
import Icon from '@able/react/dist/Icon';
import { ableTextAlias } from '../util/able-migration';

export type TabsProps = {
    onChange: (newValue: number) => void;
    children: ReactNode;
    id: string;
    activeTabIndex: number;
    developmentUrl: string;
    listDescription: string;
};

type TabProps = {
    id: string;
    panelId: string;
    title: string;
    index: number;
    setActiveTab: (index: number) => void;
    onChange?: (index: number) => void;
    activeTab: number;
    tabRefs?: React.MutableRefObject<HTMLButtonElement[]>;
    container: HTMLElement;
};

const scrollVisible = (tabButton: HTMLButtonElement, container: HTMLElement) => {
    const buttonRight = (
        document.querySelector('.ted-ui-tabs_right_button') as HTMLButtonElement
    )?.getBoundingClientRect().left;
    const buttonLeft = (
        document.querySelector('.ted-ui-tabs_left_button') as HTMLButtonElement
    )?.getBoundingClientRect().right;
    const tabButtonPosition = tabButton.getBoundingClientRect();
    if (tabButtonPosition.right > buttonRight) {
        container.scrollBy(tabButtonPosition.right - buttonRight + 8, 0);
    } else if (tabButtonPosition.left < buttonLeft) {
        container.scrollBy(tabButtonPosition.left - buttonLeft - 8, 0);
    }
};

const Tab: React.FC<TabProps> = ({
    title,
    id,
    index,
    setActiveTab,
    onChange,
    activeTab,
    tabRefs,
    panelId,
    container,
}) => {
    const clickRef = React.useRef<boolean>(false);
    return (
        <button
            id={id}
            onClick={() => {
                setActiveTab(index);
                onChange && onChange(index);
                scrollVisible(document.querySelector(`#${id}`) as HTMLButtonElement, container);
                clickRef.current = false;
            }}
            onMouseDown={() => {
                clickRef.current = true;
            }}
            onFocus={(e) => !clickRef.current && scrollVisible(e.target as HTMLButtonElement, container)}
            className={`ted-ui-tab ${activeTab === index ? 'active-tab' : ''}`}
            role="tab"
            aria-selected={`${activeTab === index ? true : false}`}
            aria-controls={`${panelId}-panel-${index}`}
            tabIndex={activeTab === index ? 0 : -1}
            ref={(ref: HTMLButtonElement) => {
                if (tabRefs && !tabRefs.current?.includes(ref) && ref !== null) {
                    tabRefs.current = [...tabRefs.current, ref];
                }
            }}
        >
            <Spacing top="spacing2x" bottom="spacing2x" left="spacing2x" right="spacing2x">
                <TextStyle alias={ableTextAlias("Label")} element="span" className="ted-ui-tab-title">
                    {title}
                </TextStyle>
            </Spacing>
        </button>
    );
};

const focusHandle = (
    event: React.KeyboardEvent<HTMLDivElement>,
    tabRefs: React.MutableRefObject<HTMLButtonElement[]>
) => {
    const index = tabRefs.current.indexOf(document.activeElement as HTMLButtonElement);
    if (event.key === 'ArrowLeft') {
        event.preventDefault();
        index > 0 ? tabRefs.current[index - 1].focus() : tabRefs.current[tabRefs.current.length - 1].focus();
    } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        index < tabRefs.current.length - 1 ? tabRefs.current[index + 1].focus() : tabRefs.current[0].focus();
    } else if (event.key === 'Home') {
        tabRefs.current[0].focus();
    } else if (event.key === 'End') {
        tabRefs.current[tabRefs.current.length - 1].focus();
    }
};

const Tabs: React.FC<TabsProps> = ({ onChange, children, activeTabIndex, id, developmentUrl, listDescription }) => {
    const tabRefs: React.MutableRefObject<HTMLButtonElement[]> = useRef([]);
    const [showButtonLeft, setShowButtonLeft] = React.useState(false);
    const [showButtonRight, setShowButtonRight] = React.useState(false);

    const divRef = React.useRef<HTMLDivElement>(null);

    const scrollFunction = () => {
        const div = divRef.current as HTMLElement;
        setShowButtonLeft(div.scrollLeft > 0);
        setShowButtonRight(div.clientWidth + Math.ceil(div.scrollLeft) < div.scrollWidth - 16);
    };

    React.useEffect(() => {
        scrollFunction();
        window.addEventListener('resize', scrollFunction);
        return () => window.removeEventListener('resize', scrollFunction);
    }, []);

    const handleRightButtonClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        const buttonLeft = (e.target as HTMLButtonElement).getBoundingClientRect().left;
        const linkList = document.querySelectorAll('.ted-ui-tab');
        for (const link of linkList) {
            const linkRight = link.getBoundingClientRect().right;
            if (linkRight > buttonLeft) {
                (divRef.current as HTMLDivElement).scrollBy(linkRight - buttonLeft + 8, 0);
                break;
            }
        }
    };

    const handleLeftButtonClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        const buttonRight = (e.target as HTMLButtonElement).getBoundingClientRect().right;
        const linkList = [...document.querySelectorAll('.ted-ui-tab')].reverse();
        for (const link of linkList) {
            const linkLeft = link.getBoundingClientRect().left;
            if (buttonRight > linkLeft) {
                (divRef.current as HTMLDivElement).scrollBy(linkLeft - buttonRight - 8, 0);
                break;
            }
        }
    };

    let tabPanels: TabPanelProps[] = [];

    const TabList = React.Children.map(children, (child, index) => {
        if (React.isValidElement(child)) {
            const panelId = `${id}-tab-${index}`;
            const tabId = `${id}-tab-${index}`;
            tabPanels = [...tabPanels, { ...child.props, id: panelId, tabId: tabId }];
            return (
                <Tab
                    id={tabId}
                    panelId={child.props.panelId}
                    title={child.props.label}
                    index={index}
                    setActiveTab={onChange}
                    activeTab={activeTabIndex}
                    tabRefs={tabRefs}
                    container={divRef.current as HTMLElement}
                />
            );
        }
        return child;
    });

    return (
        <div className="ted-ui-tabs">
            <div className="ted-ui-tabs_container">
                {showButtonLeft && (
                    <div className="ted-ui-tabs_left_button">
                        <button aria-label="Show previous tabs" onClick={handleLeftButtonClick} tabIndex={-1}>
                            <Icon icon="ChevronLeft" size="32" developmentUrl={developmentUrl} />
                        </button>
                    </div>
                )}

                <div role="tablist" className="ted-ui-tablist" onKeyDown={(e) => focusHandle(e, tabRefs)} aria-label={listDescription}>
                    <div ref={divRef} className="ted-ui-tabs_scroll" onScroll={scrollFunction}>
                        {TabList}
                    </div>
                </div>
                {showButtonRight && (
                    <div className="ted-ui-tabs_right_button">
                        <button aria-label="Show next tabs" onClick={handleRightButtonClick} tabIndex={-1}>
                            <Icon icon="ChevronRight" size="32" developmentUrl={developmentUrl} />
                        </button>
                    </div>
                )}
            </div>

            <Divider />
            {tabPanels.map((item, i) => (
                <TabPanel key={item.panelId + i} {...item} activeTabIndex={activeTabIndex} />
            ))}
            <Divider />
        </div>
    );
};

export interface TabPanelProps {
    children: ReactNode;
    index: number;
    activeTabIndex: number;
    label: string;
    panelId: string;
    tabId: string;
}

export function TabPanel(props: TabPanelProps) {
    const { children, activeTabIndex, index, panelId, tabId } = props;

    return (
        <div
            role="tabpanel"
            hidden={activeTabIndex !== index}
            id={`${panelId}-panel-${index}`}
            className={'ted-ui-tab-content'}
            aria-labelledby={tabId}
            tabIndex={activeTabIndex === index ? 0 : -1}
        >
            <Spacing top="spacing2x" bottom="spacing2x" left="spacing2x" right="spacing2x">
                {children}
            </Spacing>
        </div>
    );
}

export default Tabs;
